
import Vue, { PropType } from 'vue';
import { get, isFunction } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import Api from '@/core/api';
import { PlayerState } from '@/core/player-state';
import ClapsController, { ClapsConfig } from './claps.controller';

const THROTTLE_TIME = 60 * 1000;
const MAX_CLAPS_PER_THROTTLE = 20;

export default Vue.extend({
    name: 'Claps',

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<ClapsConfig>,
            default: () => ({})
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            controller: new ClapsController(this.api),
            visible: false,
            showThrottlingMessage: false,
            unsubscribers: [] as Unsubscriber[],
            intervals: [] as number[],
            throttleMessageTimeout: 0
        };
    },

    mounted(): void {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );

        this.updateState(state);

        this.unsubscribers.push(
            unsubscriber,
            this.api.onMetadataChange(this.onMetadataChange.bind(this)),
            this.api.onClientAction(this.onClientClaps.bind(this)),
            this.api.onSeek(this.onSeek.bind(this))
        );

        this.clapsAnimationInterval();
        this.sendClapsInterval();
        this.throttleClapsInterval();
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }

        for (const interval of this.intervals) {
            clearInterval(interval as number);
        }

        clearTimeout(this.throttleMessageTimeout);
    },

    methods: {

        onSeek() {
            this.controller.skipRound = true;
        },

        onMetadataChange(payload: unknown) {
            if (!get(this, 'config.useMetadata')) return;

            const totalClaps = get(payload, 'value.claps.totalCount');
            const partialCount = get(payload, 'value.claps.partialCount');

            if (!totalClaps) return;

            this.controller.setNewClaps(
                totalClaps as number,
                partialCount as number
            );
        },

        onClientClaps(payload: unknown, context?: string) {
            if (context !== 'claps' || typeof payload !== 'number') return;

            this.controller.setNewClaps(payload as number);
        },

        updateState(state: Partial<PlayerState>) {
            this.visible = get(state, 'isControlBarVisible', this.visible);
        },

        onClick() {
            const maxPerThrottle = get(this, 'config.maxClapsPerThrottle', MAX_CLAPS_PER_THROTTLE);

            if (this.controller.throttledClaps >= maxPerThrottle) {
                this.showThrottlingMessage = true;
                clearTimeout(this.throttleMessageTimeout);
                this.throttleMessageTimeout = setTimeout(() => {
                    this.showThrottlingMessage = false;
                }, 3000);
                return;
            }

            this.controller.incrementCounters();
            this.newClap();
        },

        newClap() {
            const container = this.$refs.animationContainer as HTMLElement;
            const clap = document.createElement('div');

            clap.classList.add('smvp-clap');
            clap.classList.add('smvp-animated');

            container.append(clap);
        },

        // Intervals

        sendClapsInterval() {
            this.intervals.push(setInterval(() => this.controller.sendClaps(), 2000));
        },

        throttleClapsInterval() {
            this.intervals.push(setInterval(() => {
                this.controller.throttledClaps = 0;
                this.showThrottlingMessage = false;
                clearTimeout(this.throttleMessageTimeout);
            }, get(this, 'config.throttleTime', THROTTLE_TIME)));
        },

        clapsAnimationInterval() {
            // every 200 ms check the clap buffer and animate the next clap
            let previousTimeStamp:number;
            let stop = false;
            const clap = (timestamp:number) => {
                if (!previousTimeStamp) {
                    previousTimeStamp = timestamp;
                }
                const elapsed = timestamp - previousTimeStamp;

                if (elapsed >= 200) {
                    previousTimeStamp = timestamp;
                    if (this.controller.consumeOneClap()) {
                        this.newClap();
                    }
                }
                if (!stop) {
                    requestAnimationFrame(clap);
                }
            };
            requestAnimationFrame(clap);

            this.unsubscribers.push(() => stop = true);
        }
    }
});
